/**
 * THE MEMBERS DIRECTORY'S RULES (ITP-E-203-02), after carbon's Members tab (OCA-E-160 `ChatPanel.tsx`, origin/main
 * 1913-2128): who is listed, under which heading, in which order, and what a search keeps. Pure, so the tab only draws
 * what these decide.
 */

/** The role headings, highest first (Plan Q4): the platform's RoleType names, Administrator shown as Admin. */
export type RoleHeading = 'Admin' | 'Manager' | 'Reviewer' | 'Contributor' | 'Editor' | 'No role';
export const ROLE_HEADINGS: readonly RoleHeading[] = ['Admin', 'Manager', 'Reviewer', 'Contributor', 'Editor', 'No role'];

/** `PlatformUserService.GetRoleType`'s role names; any other name is a Custom role and heads nothing of its own. */
const ROLE_NAMES: Readonly<Record<string, RoleHeading>> = {
  administrator: 'Admin',
  manager: 'Manager',
  reviewer: 'Reviewer',
  contributor: 'Contributor',
  editor: 'Editor'
};

/** How the list is grouped and ordered, in one control (carbon's `MEMBER_GROUPINGS`). */
export type MemberGrouping = 'type' | 'role' | 'az' | 'za';
export const MEMBER_GROUPINGS: readonly { value: MemberGrouping, label: string }[] = [
  {value: 'type', label: 'Group by type'},
  {value: 'role', label: 'Group by role'},
  {value: 'az', label: 'A to Z'},
  {value: 'za', label: 'Z to A'}
];

/** XatBot's permanent group, first in every grouping (Plan Q3). */
export const XATBOT_GROUP = 'AI Tax Assistant';
/** Everybody active in the reader's company. Firms and Orbitax wait for cross-company chat (Plan Q3, Q5). */
export const TEAM_GROUP = 'My team';

/** One person in the directory: a colleague from `Contact/AllContacts`, or XatBot. */
export interface ChatMember {
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  companyId?: string;
  /** The user's role names, as `PlatformUser.ToContact()` sends them. */
  roles?: string[] | null;
  /** Line two: the firm when there is one, else the address. */
  detail?: string;
  isXatBot?: boolean;
}

/** XatBot as the directory lists it: the AI Tax Assistant block's one row, which opens the existing XatBot. */
export const XATBOT_MEMBER: ChatMember = {
  email: 'xatbot@orbitax.com',
  name: 'XatBot',
  detail: 'xatbot@orbitax.com',
  isXatBot: true
};

/**
 * The people `Contact/AllContacts` answers with (`SharedContact`: `{contacts: [{user: Contact}]}`), as directory
 * members: the same-company, active, human users the platform holds, each with their role names and firm.
 */
export function toChatMembers(response: any): ChatMember[] {
  return ((response && response.contacts) || [])
    .map(entry => entry && entry.user)
    .filter(user => !!user && !!user.email)
    .map(user => {
      const name = (user.name || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email).trim();
      return {
        email: user.email,
        name,
        firstName: user.firstName,
        lastName: user.lastName,
        companyId: user.companyId,
        roles: Array.isArray(user.roles) ? user.roles : [],
        detail: user.firmName || user.email
      } as ChatMember;
    });
}

/** A heading and its people. A null label is the flat orderings' one group, drawn without a heading. */
export interface MemberGroup<T extends ChatMember = ChatMember> {
  id: string;
  label: string | null;
  people: T[];
}

/** The heading a user's roles put them under: the highest of them, or No role for a Custom role or none. */
export function roleRank(names: readonly string[] | null | undefined): RoleHeading {
  const ranks = (names || [])
    .map(name => ROLE_NAMES[(name || '').trim().toLowerCase()])
    .filter(Boolean)
    .map(heading => ROLE_HEADINGS.indexOf(heading));
  return ranks.length ? ROLE_HEADINGS[Math.min(...ranks)] : 'No role';
}

const byName = (a: ChatMember, b: ChatMember): number =>
  (a.name || '').localeCompare(b.name || '', undefined, {sensitivity: 'base'});

/**
 * The directory's groups. XatBot's comes first in every grouping; then My team (by type), the role headings in rank
 * order (by role), or one headless group (A to Z, Z to A). Every group is A to Z inside, except Z to A, and a group
 * with nobody in it is left out whole.
 */
export function groupMembers<T extends ChatMember>(people: readonly T[], grouping: MemberGrouping): MemberGroup<T>[] {
  const bots = people.filter(person => person.isXatBot);
  const colleagues = people.filter(person => !person.isXatBot).sort(byName);
  const groups: MemberGroup<T>[] = [{id: 'ai', label: XATBOT_GROUP, people: bots}];
  switch (grouping) {
    case 'role':
      ROLE_HEADINGS.forEach(heading => groups.push({
        id: `role-${heading}`,
        label: heading,
        people: colleagues.filter(person => roleRank(person.roles) === heading)
      }));
      break;
    case 'az':
      groups.push({id: 'all', label: null, people: colleagues});
      break;
    case 'za':
      groups.push({id: 'all', label: null, people: [...colleagues].reverse()});
      break;
    default:
      groups.push({id: 'team', label: TEAM_GROUP, people: colleagues});
  }
  return groups.filter(group => group.people.length > 0);
}

/**
 * What a search keeps: a case-insensitive match on the name or the address. XatBot is never filtered (it is always
 * reachable), and the reader is never listed at all.
 */
export function searchMembers<T extends ChatMember>(people: readonly T[], query: string, me?: string | null): T[] {
  const self = (me || '').trim().toLowerCase();
  const text = (query || '').trim().toLowerCase();
  return people.filter(person => {
    if (person.isXatBot) {
      return true;
    }
    if (self && (person.email || '').toLowerCase() === self) {
      return false;
    }
    return !text || (person.name || '').toLowerCase().includes(text) || (person.email || '').toLowerCase().includes(text);
  });
}
